import { growth, type InstallationReport } from "@bt/domain/report";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChartFrame, type Column, type Row } from "../charts/frame";
import { type HeatRow, HeatTable } from "../charts/heat-table";
import { TimeSeries } from "../charts/time-series";
import { useAdminFormat } from "../format";
import { QueryView } from "../query-view";
import { useTRPC } from "../trpc";
import { useView } from "../view";
import { Grid, HistoryFigure, ReportView, Section, Stats } from "./kit";

/** Months since a cohort was added that the cohort table shows. */
const COHORT_MONTHS = 12;
/** Cohorts the table shows, newest last. */
const COHORTS = 12;

const FUNNEL_STEPS = ["parts", "vehicle", "work_order", "completed_work_order", "order"] as const;

/** Growth (docs/admin-app/figures.md › Past growth): how many, how fast, and who stays. */
export function GrowthPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      <Section title={t("adminApp.growth.size")} description={t("adminApp.growth.sizeHint")}>
        <GrowthRates />
        <Grid>
          <HistoryFigure
            title={t("adminApp.growth.garages")}
            csvName="garages"
            figures={[{ figure: "garages.total", label: t("adminApp.growth.garages") }]}
          />
          <HistoryFigure
            title={t("adminApp.growth.users")}
            csvName="users"
            figures={[{ figure: "users.total", label: t("adminApp.growth.users") }]}
          />
          <HistoryFigure
            title={t("adminApp.growth.garagesFlow")}
            description={t("adminApp.growth.flowHint")}
            csvName="garages-added-deleted"
            figures={[
              { figure: "garages.added", label: t("adminApp.growth.added") },
              { figure: "garages.deleted", label: t("adminApp.growth.deleted") },
            ]}
          />
          <HistoryFigure
            title={t("adminApp.growth.usersFlow")}
            description={t("adminApp.growth.flowHint")}
            csvName="users-added-deleted"
            figures={[
              { figure: "users.added", label: t("adminApp.growth.added") },
              { figure: "users.deleted", label: t("adminApp.growth.deleted") },
            ]}
          />
        </Grid>
      </Section>
      <Section
        title={t("adminApp.growth.engagement")}
        description={t("adminApp.growth.engagementHint")}
      >
        <Grid>
          <HistoryFigure
            title={t("adminApp.growth.active")}
            csvName="active-users"
            figures={[
              { figure: "users.active1", label: t("adminApp.growth.dau") },
              { figure: "users.active7", label: t("adminApp.growth.wau") },
              { figure: "users.active30", label: t("adminApp.growth.mau") },
            ]}
          />
          <Stickiness />
        </Grid>
      </Section>
      <Section title={t("adminApp.growth.cohorts")} description={t("adminApp.growth.cohortsHint")}>
        <ReportView>{(report) => <Cohorts report={report} />}</ReportView>
        <ReportView>{(report) => <Funnel report={report} />}</ReportView>
      </Section>
      <Section
        title={t("adminApp.growth.segments")}
        description={t("adminApp.growth.segmentsHint")}
      >
        <HistoryFigure
          title={t("adminApp.growth.segmentsOverTime")}
          csvName="segments"
          figures={(["no_vehicle", "one", "few", "collection", "workshop"] as const).map(
            (segment) => ({
              figure: `segments.${segment}`,
              label: t(`adminApp.segments.${segment}`),
            }),
          )}
        />
      </Section>
      <Section title={t("adminApp.growth.depth")} description={t("adminApp.growth.depthHint")}>
        <Grid>
          <HistoryFigure
            title={t("adminApp.growth.workDone")}
            csvName="work-done"
            figures={[
              { figure: "workOrders.completed", label: t("adminApp.growth.completed") },
              { figure: "orders.placed", label: t("adminApp.growth.placed") },
              { figure: "garages.active30", label: t("adminApp.growth.activeGarages") },
            ]}
          />
          <ReportView>{(report) => <SpendPerMonth report={report} />}</ReportView>
        </Grid>
      </Section>
    </div>
  );
}

/** Garages and users now, and their change over the range. */
function GrowthRates() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useAdminFormat();
  const { installation, range } = useView();
  const query = useQuery(
    trpc.installation.history.queryOptions({
      name: installation,
      range,
      figures: ["garages.total", "users.total", "garages.active30", "users.active30"],
    }),
  );
  return (
    <QueryView query={query}>
      {(data) => (
        <Stats
          items={data.series.map((series) => {
            const values = (series.points as (number | null)[]).filter(
              (p): p is number => typeof p === "number",
            );
            const change = values.length ? growth([values[0] ?? 0, values.at(-1) ?? 0]) : null;
            return {
              key: series.figure,
              label: t(
                `adminApp.growth.stat.${series.figure.replace(".", "_") as "garages_total"}`,
              ),
              value: format.count(values.at(-1) ?? null),
              detail: change
                ? t("adminApp.growth.overRange", { change: format.growth(change) })
                : undefined,
            };
          })}
        />
      )}
    </QueryView>
  );
}

/** DAU over MAU per period. */
function Stickiness() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useAdminFormat();
  const { installation, range } = useView();
  const query = useQuery(trpc.engagement.queryOptions({ name: installation, range }));
  return (
    <QueryView query={query}>
      {(data) => {
        const points = data.stickiness.map((s) =>
          s?.percent === null || !s ? null : s.percent / 100,
        );
        const columns: Column[] = [
          {
            key: "period",
            label: t("adminApp.health.period"),
            format: (v) => format.period(String(v)),
          },
          { key: "meanDau", label: t("adminApp.growth.meanDau"), numeric: true },
          { key: "mau", label: t("adminApp.growth.mau"), numeric: true },
          { key: "stickiness", label: t("adminApp.growth.stickiness"), numeric: true },
        ];
        const rows: Row[] = data.periods.map((period, i) => ({
          period,
          meanDau: data.meanDau[i] ?? null,
          mau: data.mau[i] ?? null,
          stickiness: data.stickiness[i]?.percent ?? null,
        }));
        return (
          <ChartFrame
            title={t("adminApp.growth.stickiness")}
            description={t("adminApp.growth.stickinessHint")}
            csvName="stickiness"
            columns={columns}
            rows={rows}
          >
            <TimeSeries
              periods={data.periods}
              series={[{ key: "stickiness", label: t("adminApp.growth.stickiness"), points }]}
              formatValue={format.percent}
              formatPeriod={format.period}
              label={t("adminApp.growth.stickiness")}
              height={180}
            />
          </ChartFrame>
        );
      }}
    </QueryView>
  );
}

/** Garages by the month they were added, and the share of them active in each month since. */
function Cohorts({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const cohorts = (report.garages?.cohorts ?? []).filter((c) => c.added > 0).slice(-COHORTS);
  const columns = Array.from({ length: COHORT_MONTHS }, (_, i) => ({
    key: String(i),
    label: String(i),
  }));
  const rows: HeatRow[] = cohorts.map((cohort) => ({
    key: cohort.month,
    label: format.period(cohort.month),
    detail: format.count(cohort.added),
    cells: columns.map((_, i) => {
      const active = cohort.active[i];
      if (active === undefined) return null;
      const share = cohort.added ? active / cohort.added : 0;
      return { share, text: format.percent(share), title: `${active} / ${cohort.added}` };
    }),
  }));
  return (
    <ChartFrame
      title={t("adminApp.growth.cohortTable")}
      description={t("adminApp.growth.cohortTableHint")}
      csvName="cohorts"
      columns={[
        { key: "month", label: t("adminApp.growth.month") },
        { key: "added", label: t("adminApp.growth.added"), numeric: true },
        ...columns.map((c) => ({ key: `m${c.key}`, label: c.key, numeric: true })),
      ]}
      rows={cohorts.map((c) => ({
        month: c.month,
        added: c.added,
        ...Object.fromEntries(columns.map((col, i) => [`m${col.key}`, c.active[i] ?? null])),
      }))}
    >
      {rows.length ? (
        <HeatTable
          caption={t("adminApp.growth.cohortTable")}
          rowHeader={t("adminApp.growth.month")}
          detailHeader={t("adminApp.growth.added")}
          columns={columns}
          rows={rows}
          legend={["0 %", "100 %"]}
          compact
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t("adminApp.chart.noValues")}</p>
      )}
    </ChartFrame>
  );
}

/** Of the garages added in a month, those that reached each step within 30 days. */
function Funnel({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const funnel = (report.garages?.funnel ?? []).slice(-COHORTS);
  const columns = FUNNEL_STEPS.map((step) => ({
    key: step,
    label: t(`adminApp.growth.steps.${step}`),
  }));
  const rows: HeatRow[] = funnel.map((month) => ({
    key: month.month,
    label: format.period(month.month),
    detail: format.count(month.added),
    cells: FUNNEL_STEPS.map((step) => {
      const reached = month.steps.find((s) => s.step === step);
      if (!reached) return null;
      const share = month.added ? (reached.reached ?? 0) / month.added : 0;
      return {
        share,
        text: format.percent(share),
        title: t("adminApp.growth.daysToStep", {
          reached: reached.reached ?? 0,
          days: format.decimal(reached.medianDays ?? 0),
        }),
      };
    }),
  }));
  return (
    <ChartFrame
      title={t("adminApp.growth.funnel")}
      description={t("adminApp.growth.funnelHint")}
      csvName="funnel"
      columns={[
        { key: "month", label: t("adminApp.growth.month") },
        { key: "added", label: t("adminApp.growth.added"), numeric: true },
        ...FUNNEL_STEPS.flatMap((step) => [
          { key: step, label: t(`adminApp.growth.steps.${step}`), numeric: true },
          { key: `${step}Days`, label: t("adminApp.growth.medianDays"), numeric: true },
        ]),
      ]}
      rows={funnel.map((month) => ({
        month: month.month,
        added: month.added,
        ...Object.fromEntries(
          FUNNEL_STEPS.flatMap((step) => {
            const s = month.steps.find((x) => x.step === step);
            return [
              [step, s?.reached ?? null],
              [`${step}Days`, s?.medianDays ?? null],
            ];
          }),
        ),
      }))}
    >
      {rows.length ? (
        <HeatTable
          caption={t("adminApp.growth.funnel")}
          rowHeader={t("adminApp.growth.month")}
          detailHeader={t("adminApp.growth.added")}
          columns={columns}
          rows={rows}
          legend={["0 %", "100 %"]}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t("adminApp.chart.noValues")}</p>
      )}
    </ChartFrame>
  );
}

/** Placed orders per month, without tax, one line per currency; no exchange rates. */
function SpendPerMonth({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const rows = report.spend?.perMonth ?? [];
  const months = [...new Set(rows.map((r) => r.month))].sort().slice(-COHORT_MONTHS);
  const currencies = [...new Set(rows.map((r) => r.currency))].sort().slice(0, 4);
  const series = currencies.map((currency) => ({
    key: currency,
    label: currency,
    points: months.map(
      (month) => rows.find((r) => r.month === month && r.currency === currency)?.amountMinor ?? 0,
    ),
  }));
  const single = currencies.length === 1 ? currencies[0] : null;
  return (
    <ChartFrame
      title={t("adminApp.growth.spend")}
      description={t("adminApp.growth.spendHint")}
      csvName="spend-per-month"
      columns={[
        { key: "month", label: t("adminApp.growth.month") },
        { key: "currency", label: t("adminApp.overview.currency") },
        { key: "amountMinor", label: t("adminApp.growth.amountMinor"), numeric: true },
        { key: "orders", label: t("adminApp.growth.orders"), numeric: true },
      ]}
      rows={rows.map((r) => ({
        month: r.month,
        currency: r.currency,
        amountMinor: r.amountMinor,
        orders: r.orders,
      }))}
    >
      {months.length ? (
        <TimeSeries
          periods={months}
          series={series}
          formatValue={(n) => (single ? format.money(n, single) : format.compact(n))}
          formatPeriod={format.period}
          label={t("adminApp.growth.spend")}
          height={180}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t("adminApp.chart.noValues")}</p>
      )}
    </ChartFrame>
  );
}
